<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { CommerceService } from '@/services/commerce.services';
import axiosInstance from '@/services/interceptor.services';
import axios from 'axios';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import { TranslationService } from '@/services/translation.services';
import { token } from '@/refs/common.refs';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);

const {
  data: store,
  isPending,
  isError,
} = useQuery({
  queryKey: computed(() => ['commerce-detail', id.value]),
  queryFn: () =>
    CommerceService.getStoreById(id.value).then((r) => {
      const d = r.data as any;
      return d?.data ?? d;
    }),
  enabled: computed(() => !!id.value),
});

function invalidate() {
  queryClient.invalidateQueries({ queryKey: ['commerce-detail', id.value] });
  queryClient.invalidateQueries({ queryKey: ['commerces-page'] });
  queryClient.invalidateQueries({ queryKey: ['commerces'] });
  queryClient.invalidateQueries({ queryKey: ['GetRestaurants'] });
}

/* ── Constantes ──────────────────────────────────────────────────────────── */
const CURRENCIES = computed(() => [
  { value: 'EUR', label: t('editCommerce.currencies.EUR') },
  { value: 'USD', label: t('editCommerce.currencies.USD') },
  { value: 'GBP', label: t('editCommerce.currencies.GBP') },
  { value: 'CHF', label: t('editCommerce.currencies.CHF') },
  { value: 'MAD', label: t('editCommerce.currencies.MAD') },
  { value: 'XOF', label: t('editCommerce.currencies.XOF') },
]);
const TIMEZONES = computed(() => [
  {
    value: 'Europe/Brussels',
    label: t('editCommerce.timezones.EuropeBrussels'),
  },
  { value: 'Europe/London', label: t('editCommerce.timezones.EuropeLondon') },
  {
    value: 'Europe/Amsterdam',
    label: t('editCommerce.timezones.EuropeAmsterdam'),
  },
  { value: 'Europe/Zurich', label: t('editCommerce.timezones.EuropeZurich') },
  {
    value: 'Africa/Casablanca',
    label: t('editCommerce.timezones.AfricaCasablanca'),
  },
  { value: 'Africa/Abidjan', label: t('editCommerce.timezones.AfricaAbidjan') },
  {
    value: 'America/New_York',
    label: t('editCommerce.timezones.AmericaNewYork'),
  },
  {
    value: 'America/Toronto',
    label: t('editCommerce.timezones.AmericaToronto'),
  },
  { value: 'UTC', label: t('editCommerce.timezones.UTC') },
]);
const COUNTRIES = computed(() => [
  { value: 'BE', label: t('editCommerce.countries.BE') },
  { value: 'FR', label: t('editCommerce.countries.FR') },
  { value: 'NL', label: t('editCommerce.countries.NL') },
  { value: 'LU', label: t('editCommerce.countries.LU') },
  { value: 'DE', label: t('editCommerce.countries.DE') },
  { value: 'CH', label: t('editCommerce.countries.CH') },
  { value: 'GB', label: t('editCommerce.countries.GB') },
  { value: 'US', label: t('editCommerce.countries.US') },
  { value: 'CA', label: t('editCommerce.countries.CA') },
  { value: 'MA', label: t('editCommerce.countries.MA') },
  { value: 'CI', label: t('editCommerce.countries.CI') },
  { value: 'SN', label: t('editCommerce.countries.SN') },
]);
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
const WEEKDAY_LABELS = computed<Record<string, string>>(() => ({
  monday: t('days.monday'),
  tuesday: t('days.tuesday'),
  wednesday: t('days.wednesday'),
  thursday: t('days.thursday'),
  friday: t('days.friday'),
  saturday: t('days.saturday'),
  sunday: t('days.sunday'),
}));
const POSITIONS = computed(() => [
  { value: 'manager', label: t('editCommerce.positions.manager') },
  { value: 'ceo', label: t('editCommerce.positions.ceo') },
  { value: 'cfo', label: t('editCommerce.positions.cfo') },
  { value: 'cto', label: t('editCommerce.positions.cto') },
  { value: 'accountant', label: t('editCommerce.positions.accountant') },
  {
    value: 'legal_representative',
    label: t('editCommerce.positions.legal_representative'),
  },
  {
    value: 'technical_contact',
    label: t('editCommerce.positions.technical_contact'),
  },
  {
    value: 'billing_contact',
    label: t('editCommerce.positions.billing_contact'),
  },
  {
    value: 'support_contact',
    label: t('editCommerce.positions.support_contact'),
  },
]);

/* ── Traduction (backend interne) ────────────────────────────────────────── */
async function translateFields(
  src: 'fr' | 'en' | 'nl',
  getText: () => string,
  setText: (lang: 'fr' | 'en' | 'nl', val: string) => void,
  translatingRef: { value: boolean },
) {
  const text = getText();
  if (!text.trim() || translatingRef.value) return;
  translatingRef.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((lang) => setText(lang, translated[lang]));
  } catch {
    /* silencieux */
  } finally {
    translatingRef.value = false;
  }
}

/* ── Étape courante ──────────────────────────────────────────────────────── */
const step = ref(1);

/* ── Étape 1 : Informations ──────────────────────────────────────────────── */
const form1 = ref({
  designation: '',
  orderCode: '',
  currency: 'EUR',
  timezone: 'Europe/Brussels',
  country: 'BE',
  address: '',
  preparationTimeBuffer: 15,
  marketPayShopId: '',
  bdLang: 'fr' as 'fr' | 'en' | 'nl',
  bdFr: '',
  bdEn: '',
  bdNl: '',
  descLang: 'fr' as 'fr' | 'en' | 'nl',
  descFr: '',
  descEn: '',
  descNl: '',
  tagLang: 'fr' as 'fr' | 'en' | 'nl',
  tagFr: '',
  tagEn: '',
  tagNl: '',
  city: '',
  foundedYear: '',
});

watch(
  () => store.value,
  (val) => {
    if (!val) return;
    const s = val;
    // bannerDescription : string racine ou objet alternatives
    const bdAlt = s.bannerDescriptionAlternatives ?? {};
    const bdRaw = s.bannerDescription;
    const bd = {
      fr: bdAlt.fr ?? (typeof bdRaw === 'string' ? bdRaw : (bdRaw?.fr ?? '')),
      en: bdAlt.en ?? (typeof bdRaw === 'string' ? '' : (bdRaw?.en ?? '')),
      nl: bdAlt.nl ?? (typeof bdRaw === 'string' ? '' : (bdRaw?.nl ?? '')),
    };
    // taglineCodes : tableau simple ou objet par langue dans taglineCodesAlternatives
    const meta = s.metadata ?? {};
    const tcAlt = meta.taglineCodesAlternatives ?? {};
    const tc = {
      fr:
        tcAlt.fr ?? (Array.isArray(meta.taglineCodes) ? meta.taglineCodes : []),
      en: tcAlt.en ?? [],
      nl: tcAlt.nl ?? [],
    };
    form1.value = {
      designation: s.designation ?? '',
      orderCode: s.orderCode ?? '',
      currency: s.currencyCode ?? s.currency?.code ?? s.currency ?? 'EUR',
      timezone: s.timezone?.name ?? s.timezone ?? 'Europe/Brussels',
      country: s.country ?? 'BE',
      address: s.address ?? '',
      preparationTimeBuffer: s.preparationTimeBuffer ?? 15,
      marketPayShopId: s.marketPayShopId ?? '',
      bdLang: 'fr',
      bdFr: bd.fr,
      bdEn: bd.en,
      bdNl: bd.nl,
      descLang: 'fr',
      descFr: s.description?.fr ?? '',
      descEn: s.description?.en ?? '',
      descNl: s.description?.nl ?? '',
      tagLang: 'fr',
      tagFr: tc.fr.join(', '),
      tagEn: tc.en.join(', '),
      tagNl: tc.nl.join(', '),
      city: meta.city ?? '',
      foundedYear: meta.foundedYear ? String(meta.foundedYear) : '',
    };
  },
  { immediate: true },
);

/* ── Logo upload ─────────────────────────────────────────────────────────── */
const logoFile = ref<File | null>(null);
const logoPreview = ref<string | null>(null);
const logoUploading = ref(false);
const logoInputRef = ref<HTMLInputElement | null>(null);

const existingLogoUrl = computed(
  () => store.value?.logo?.info?.secureUrl ?? store.value?.logoUrl ?? null,
);

function onLogoFileChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const f = input.files?.[0] ?? null;
  if (!f) return;
  logoFile.value = f;
  const reader = new FileReader();
  reader.onload = (ev) => {
    logoPreview.value = ev.target?.result as string;
  };
  reader.readAsDataURL(f);
}

function removeLogo() {
  logoFile.value = null;
  logoPreview.value = null;
  if (logoInputRef.value) logoInputRef.value.value = '';
}

async function uploadLogoIfNeeded(): Promise<string | null> {
  if (!logoFile.value) return null;
  logoUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', logoFile.value);
    fd.append('designation', `logo-${id.value}`);
    fd.append('restaurantId', id.value);
    const accessToken = token.value?.accessToken;
    const r = await axios.post(`${VITE_API_URL}files/upload`, fd, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const d = r.data as any;
    return d?.data?.id ?? d?.id ?? null;
  } finally {
    logoUploading.value = false;
  }
}

// N'affiche les erreurs de champ (bordure rouge + message) qu'après une
// première tentative de soumission, pas dès l'ouverture du formulaire.
const attemptedSubmit1 = ref(false);
const showNameError = computed(
  () => attemptedSubmit1.value && !form1.value.designation.trim(),
);

const isSaving1 = ref(false);
const error1 = ref<string | null>(null);
const translatingBd = ref(false);
const translatingDesc = ref(false);
const translatingTag = ref(false);

function translateBd() {
  const src = form1.value.bdLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.bdFr
        : src === 'en'
          ? form1.value.bdEn
          : form1.value.bdNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.bdFr = val;
      if (lang === 'en') form1.value.bdEn = val;
      if (lang === 'nl') form1.value.bdNl = val;
    },
    translatingBd,
  );
}
function translateDesc() {
  const src = form1.value.descLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.descFr
        : src === 'en'
          ? form1.value.descEn
          : form1.value.descNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.descFr = val;
      if (lang === 'en') form1.value.descEn = val;
      if (lang === 'nl') form1.value.descNl = val;
    },
    translatingDesc,
  );
}
function translateTag() {
  const src = form1.value.tagLang;
  return translateFields(
    src,
    () =>
      src === 'fr'
        ? form1.value.tagFr
        : src === 'en'
          ? form1.value.tagEn
          : form1.value.tagNl,
    (lang, val) => {
      if (lang === 'fr') form1.value.tagFr = val;
      if (lang === 'en') form1.value.tagEn = val;
      if (lang === 'nl') form1.value.tagNl = val;
    },
    translatingTag,
  );
}

async function submitStep1() {
  attemptedSubmit1.value = true;
  if (!form1.value.designation.trim() || isSaving1.value) return;
  error1.value = null;
  isSaving1.value = true;
  try {
    const logoId = await uploadLogoIfNeeded();
    const body: any = {
      designation: form1.value.designation.trim(),
      timezone: form1.value.timezone || undefined,
      address: form1.value.address.trim() || undefined,
      country: form1.value.country || undefined,
      marketPayShopId: form1.value.marketPayShopId.trim() || undefined,
      preparationTimeBuffer: form1.value.preparationTimeBuffer || undefined,
      bannerDescription: {
        fr: form1.value.bdFr || '',
        en: form1.value.bdEn || '',
        nl: form1.value.bdNl || '',
      },
    };
    if (logoId) body.logoId = logoId;
    if (
      form1.value.descFr.trim() ||
      form1.value.descEn.trim() ||
      form1.value.descNl.trim()
    ) {
      body.description = {
        fr: form1.value.descFr,
        en: form1.value.descEn,
        nl: form1.value.descNl,
      };
    }
    const meta: any = {};
    if (form1.value.city.trim()) meta.city = form1.value.city.trim();
    if (form1.value.foundedYear)
      meta.foundedYear = Number(form1.value.foundedYear);
    if (
      form1.value.tagFr.trim() ||
      form1.value.tagEn.trim() ||
      form1.value.tagNl.trim()
    ) {
      meta.taglineCodes = {
        fr: form1.value.tagFr
          ? form1.value.tagFr
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        en: form1.value.tagEn
          ? form1.value.tagEn
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        nl: form1.value.tagNl
          ? form1.value.tagNl
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };
    }
    if (Object.keys(meta).length) body.metadata = meta;
    await axiosInstance.put(`restaurants/update/${id.value}`, body);
    invalidate();
    step.value = 2;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error1.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? t('editCommerce.error1Fallback'));
  } finally {
    isSaving1.value = false;
  }
}

/* ── Étape 2 : Horaires ──────────────────────────────────────────────────── */
type DaySchedule = {
  id?: string;
  enabled: boolean;
  open: string;
  close: string;
};
type WeekSchedule = Record<string, DaySchedule>;

const weekSchedule = ref<WeekSchedule>({
  monday: { enabled: true, open: '09:00', close: '22:00' },
  tuesday: { enabled: true, open: '09:00', close: '22:00' },
  wednesday: { enabled: true, open: '09:00', close: '22:00' },
  thursday: { enabled: true, open: '09:00', close: '22:00' },
  friday: { enabled: true, open: '09:00', close: '22:00' },
  saturday: { enabled: false, open: '10:00', close: '23:00' },
  sunday: { enabled: false, open: '10:00', close: '23:00' },
});

watch(
  () => store.value,
  (val) => {
    if (!val) return;
    const schedules = val.schedules ?? [];
    if (!schedules.length) return;
    for (const day of WEEKDAYS) {
      const sch = schedules.find((x: any) => x.weekday === day);
      if (sch) {
        weekSchedule.value[day] = {
          id: sch.id,
          enabled: !sch.disabledAt,
          open: (sch.openTime ?? sch.open ?? '09:00').slice(0, 5),
          close: (sch.closeTime ?? sch.close ?? '22:00').slice(0, 5),
        };
      }
    }
  },
  { immediate: true },
);

const timePickerOpen = ref<string | null>(null);
function parseTime(t: string) {
  const [h, m] = t.split(':').map(Number);
  return { h: h ?? 0, m: m ?? 0 };
}
function fmtTime(h: number, m: number) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function adjustHour(day: string, field: 'open' | 'close', delta: number) {
  const t = parseTime(weekSchedule.value[day][field]);
  weekSchedule.value[day][field] = fmtTime((t.h + delta + 24) % 24, t.m);
}
function adjustMin(day: string, field: 'open' | 'close', delta: number) {
  const t = parseTime(weekSchedule.value[day][field]);
  weekSchedule.value[day][field] = fmtTime(t.h, (t.m + delta + 60) % 60);
}
function toggleTimePicker(key: string) {
  timePickerOpen.value = timePickerOpen.value === key ? null : key;
}
function closeTimePicker() {
  timePickerOpen.value = null;
}

const isSaving2 = ref(false);
const error2 = ref<string | null>(null);

async function submitStep2() {
  isSaving2.value = true;
  error2.value = null;
  try {
    const enabled = WEEKDAYS.filter((d) => weekSchedule.value[d].enabled);
    const body: Record<string, { open: string; close: string }[]> = {};
    for (const d of enabled) {
      body[d] = [
        {
          open: weekSchedule.value[d].open,
          close: weekSchedule.value[d].close,
        },
      ];
    }
    await axiosInstance.put(
      `restaurants/${id.value}/schedules/weekday-schedule`,
      body,
    );
    invalidate();
    step.value = 3;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error2.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? t('editCommerce.error2Fallback'));
  } finally {
    isSaving2.value = false;
  }
}

/* ── Étape 3 : Contacts ──────────────────────────────────────────────────── */
const contacts = computed<any[]>(() => store.value?.contacts ?? []);
const showContactForm = ref(false);
const editContactId = ref<string | null>(null);
const contactSaving = ref(false);
const contactError = ref<string | null>(null);
const confirmContact = ref<any | null>(null);
const deletingContact = ref(false);
const contactForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: 'manager',
  isPrimary: false,
});

// N'affiche les erreurs de champ (bordure rouge + message) qu'après une
// première tentative de soumission, pas dès l'ouverture du formulaire.
const attemptedSubmitContact = ref(false);
const showContactFirstNameError = computed(
  () => attemptedSubmitContact.value && !contactForm.value.firstName.trim(),
);
const showContactLastNameError = computed(
  () => attemptedSubmitContact.value && !contactForm.value.lastName.trim(),
);

function openAddContact() {
  editContactId.value = null;
  contactForm.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'manager',
    isPrimary: false,
  };
  contactError.value = null;
  attemptedSubmitContact.value = false;
  showContactForm.value = true;
}
function openEditContact(c: any) {
  editContactId.value = c.id;
  contactForm.value = {
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    position: c.position ?? 'manager',
    isPrimary: !!c.isPrimary,
  };
  contactError.value = null;
  attemptedSubmitContact.value = false;
  showContactForm.value = true;
}
function cancelContact() {
  showContactForm.value = false;
  editContactId.value = null;
}

async function saveContact() {
  attemptedSubmitContact.value = true;
  if (
    !contactForm.value.firstName.trim() ||
    !contactForm.value.lastName.trim()
  ) {
    contactError.value = t('editCommerce.contactErrorRequired');
    return;
  }
  contactSaving.value = true;
  contactError.value = null;
  try {
    const payload: any = {
      firstName: contactForm.value.firstName.trim(),
      lastName: contactForm.value.lastName.trim(),
      position: contactForm.value.position,
      isPrimary: contactForm.value.isPrimary,
    };
    if (contactForm.value.email.trim())
      payload.email = contactForm.value.email.trim();
    if (contactForm.value.phone.trim())
      payload.phone = contactForm.value.phone.trim();
    if (editContactId.value) {
      await axiosInstance.put(
        `restaurants/${id.value}/contacts/update/${editContactId.value}`,
        payload,
      );
    } else {
      await axiosInstance.post(`restaurants/${id.value}/contacts/add`, payload);
    }
    invalidate();
    showContactForm.value = false;
    editContactId.value = null;
  } catch (err: any) {
    const r = err?.response?.data?.message;
    contactError.value = Array.isArray(r)
      ? r[0]
      : (r ?? err?.message ?? t('editCommerce.contactErrorFallback'));
  } finally {
    contactSaving.value = false;
  }
}

async function confirmDeleteContact() {
  if (!confirmContact.value) return;
  deletingContact.value = true;
  try {
    await axiosInstance.delete(
      `restaurants/${id.value}/contacts/delete/${confirmContact.value.id}`,
    );
    invalidate();
  } catch {
  } finally {
    deletingContact.value = false;
    confirmContact.value = null;
  }
}

const POSITION_LABELS = computed<Record<string, string>>(() =>
  Object.fromEntries(POSITIONS.value.map((p) => [p.value, p.label])),
);

function initials(c: any) {
  if (c.firstName || c.lastName)
    return (
      ((c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '')).toUpperCase() || '?'
    );
  if (c.names) return c.names.slice(0, 2).toUpperCase();
  return '?';
}
function displayName(c: any) {
  if (c.firstName || c.lastName)
    return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
  return c.names ?? '—';
}
</script>

<template>
  <div class="ecp" @click="closeTimePicker">
    <!-- Breadcrumb -->
    <nav class="ecp__breadcrumb">
      <button class="ecp__bc-link" @click="router.push('/commerces')">
        {{ t('editCommerce.breadcrumb') }}
      </button>
      <i class="pi pi-angle-right ecp__bc-sep" />
      <button class="ecp__bc-link" @click="router.push(`/commerces/${id}`)">
        {{ store?.designation ?? '…' }}
      </button>
      <i class="pi pi-angle-right ecp__bc-sep" />
      <span class="ecp__bc-cur">{{ t('editCommerce.bcEdit') }}</span>
    </nav>

    <!-- Skeleton -->
    <div v-if="isPending" class="ecp__skeleton">
      <div class="ecp__sk ecp__sk--h" />
      <div class="ecp__sk ecp__sk--b" />
    </div>

    <!-- Erreur chargement -->
    <div v-else-if="isError" class="ecp__load-error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('editCommerce.loadError') }}</p>
      <button class="ecp__btn ecp__btn--ghost" @click="router.back()">
        <i class="pi pi-arrow-left" /> {{ t('editCommerce.back') }}
      </button>
    </div>

    <template v-else-if="store">
      <!-- Header + stepper -->
      <div class="ecp__header">
        <button
          class="ecp__back"
          @click="step > 1 ? step-- : router.push(`/commerces/${id}`)"
        >
          <i class="pi pi-arrow-left" />
        </button>
        <div class="ecp__header-text">
          <h1 class="ecp__title">{{ t('editCommerce.title') }}</h1>
          <p class="ecp__sub">{{ store.designation }}</p>
        </div>
        <div class="ecp__stepper">
          <div
            v-for="s in 3"
            :key="s"
            class="ecp__step"
            :class="{
              'ecp__step--done': step > s,
              'ecp__step--active': step === s,
            }"
          >
            <div class="ecp__step-dot">
              <i v-if="step > s" class="pi pi-check" /><span v-else>{{
                s
              }}</span>
            </div>
            <span class="ecp__step-label">{{
              [
                t('editCommerce.steps.infos'),
                t('editCommerce.steps.horaires'),
                t('editCommerce.steps.contacts'),
              ][s - 1]
            }}</span>
          </div>
        </div>
      </div>

      <!-- ═══ ÉTAPE 1 : Informations ═══ -->
      <div v-if="step === 1" class="ecp__body">
        <!-- Infos générales -->
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-shop" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editCommerce.step1Title') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editCommerce.step1Sub') }}
              </p>
            </div>
          </div>
          <div class="ecp__fields">
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field ecp__field--span2">
                <label class="ecp__label">{{
                  t('editCommerce.designation')
                }}</label>
                <input
                  v-model="form1.designation"
                  class="ecp__input"
                  :class="{ 'ecp__input--error': showNameError }"
                  type="text"
                  :placeholder="t('editCommerce.namePlaceholder')"
                />
                <p v-if="showNameError" class="ecp__field-error">
                  {{ t('editCommerce.requiredField') }}
                </p>
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.orderCode')
                }}</label>
                <input
                  :value="form1.orderCode"
                  class="ecp__input"
                  disabled
                  style="
                    opacity: 0.5;
                    cursor: not-allowed;
                    text-transform: uppercase;
                    letter-spacing: 0.15em;
                  "
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.currency')
                }}</label>
                <input
                  :value="form1.currency"
                  class="ecp__input"
                  disabled
                  style="opacity: 0.5; cursor: not-allowed"
                />
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.timezone')
                }}</label>
                <div class="ecp__select-wrap">
                  <select v-model="form1.timezone" class="ecp__select">
                    <option
                      v-for="tz in TIMEZONES"
                      :key="tz.value"
                      :value="tz.value"
                    >
                      {{ tz.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ecp__select-icon" />
                </div>
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{ t('editCommerce.buffer') }}</label>
                <input
                  v-model.number="form1.preparationTimeBuffer"
                  class="ecp__input"
                  type="number"
                  min="3"
                  :placeholder="t('editCommerce.bufferPlaceholder')"
                />
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.address')
                }}</label>
                <input
                  v-model="form1.address"
                  class="ecp__input"
                  type="text"
                  :placeholder="t('editCommerce.addressPlaceholder')"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.country')
                }}</label>
                <div class="ecp__select-wrap">
                  <select v-model="form1.country" class="ecp__select">
                    <option
                      v-for="c in COUNTRIES"
                      :key="c.value"
                      :value="c.value"
                    >
                      {{ c.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ecp__select-icon" />
                </div>
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field">
                <label class="ecp__label">{{ t('editCommerce.city') }}</label>
                <input
                  v-model="form1.city"
                  class="ecp__input"
                  type="text"
                  :placeholder="t('editCommerce.cityPlaceholder')"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editCommerce.foundedYear')
                }}</label>
                <input
                  v-model="form1.foundedYear"
                  class="ecp__input"
                  type="number"
                  min="1900"
                  max="2100"
                  :placeholder="t('editCommerce.yearPlaceholder')"
                />
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--2">
              <div class="ecp__field">
                <label class="ecp__label">{{ t('editCommerce.shopId') }}</label>
                <input
                  v-model="form1.marketPayShopId"
                  class="ecp__input"
                  type="text"
                  :placeholder="t('editCommerce.shopIdPlaceholder')"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="ecp__divider" />

        <!-- Taglines -->
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-tag" /></div>
            <div>
              <p class="ecp__section-title">{{ t('editCommerce.taglines') }}</p>
              <p class="ecp__section-sub">
                {{ t('editCommerce.taglinesSub') }}
              </p>
            </div>
          </div>
          <div class="ecp__fields">
            <div class="ecp__translabel-row">
              <label class="ecp__label">{{
                t('editCommerce.inputLang')
              }}</label>
              <div class="ecp__select-wrap" style="max-width: 10rem">
                <select
                  v-model="form1.tagLang"
                  class="ecp__select ecp__select--sm"
                >
                  <option value="fr">{{ t('editCommerce.langFr') }}</option>
                  <option value="en">{{ t('editCommerce.langEn') }}</option>
                  <option value="nl">{{ t('editCommerce.langNl') }}</option>
                </select>
                <i class="pi pi-chevron-down ecp__select-icon" />
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--3">
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.tagLang === 'fr' ? 'ecp__lang-badge--active' : ''
                    "
                    >FR</span
                  >
                  <span
                    v-if="translatingTag && form1.tagLang !== 'fr'"
                    class="ecp__translating-dot"
                  />
                </label>
                <input
                  v-model="form1.tagFr"
                  class="ecp__input"
                  :class="form1.tagLang === 'fr' ? 'ecp__input--primary' : ''"
                  type="text"
                  :placeholder="t('editCommerce.taglineFrPlaceholder')"
                  @blur="form1.tagLang === 'fr' && translateTag()"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.tagLang === 'en' ? 'ecp__lang-badge--active' : ''
                    "
                    >EN</span
                  >
                  <span
                    v-if="translatingTag && form1.tagLang !== 'en'"
                    class="ecp__translating-dot"
                  />
                </label>
                <input
                  v-model="form1.tagEn"
                  class="ecp__input"
                  :class="form1.tagLang === 'en' ? 'ecp__input--primary' : ''"
                  type="text"
                  :placeholder="t('editCommerce.taglineEnPlaceholder')"
                  @blur="form1.tagLang === 'en' && translateTag()"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.tagLang === 'nl' ? 'ecp__lang-badge--active' : ''
                    "
                    >NL</span
                  >
                  <span
                    v-if="translatingTag && form1.tagLang !== 'nl'"
                    class="ecp__translating-dot"
                  />
                </label>
                <input
                  v-model="form1.tagNl"
                  class="ecp__input"
                  :class="form1.tagLang === 'nl' ? 'ecp__input--primary' : ''"
                  type="text"
                  :placeholder="t('editCommerce.taglineNlPlaceholder')"
                  @blur="form1.tagLang === 'nl' && translateTag()"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="ecp__divider" />

        <!-- Banner Description -->
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-image" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editCommerce.bannerDesc') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editCommerce.bannerDescSub') }}
              </p>
            </div>
          </div>
          <div class="ecp__fields">
            <div class="ecp__translabel-row">
              <label class="ecp__label">Langue de saisie</label>
              <div class="ecp__select-wrap" style="max-width: 10rem">
                <select
                  v-model="form1.bdLang"
                  class="ecp__select ecp__select--sm"
                >
                  <option value="fr">{{ t('editCommerce.langFr') }}</option>
                  <option value="en">{{ t('editCommerce.langEn') }}</option>
                  <option value="nl">{{ t('editCommerce.langNl') }}</option>
                </select>
                <i class="pi pi-chevron-down ecp__select-icon" />
              </div>
            </div>
            <div class="ecp__field-row ecp__field-row--3">
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.bdLang === 'fr' ? 'ecp__lang-badge--active' : ''
                    "
                    >FR</span
                  >
                  {{ t('editCommerce.langFr') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'fr'"
                    class="ecp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdFr"
                  class="ecp__textarea"
                  :class="form1.bdLang === 'fr' ? 'ecp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('editCommerce.bannerFrPlaceholder')"
                  @blur="form1.bdLang === 'fr' && translateBd()"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.bdLang === 'en' ? 'ecp__lang-badge--active' : ''
                    "
                    >EN</span
                  >
                  {{ t('editCommerce.langEn') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'en'"
                    class="ecp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdEn"
                  class="ecp__textarea"
                  :class="form1.bdLang === 'en' ? 'ecp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('editCommerce.bannerEnPlaceholder')"
                  @blur="form1.bdLang === 'en' && translateBd()"
                />
              </div>
              <div class="ecp__field">
                <label class="ecp__label">
                  <span
                    class="ecp__lang-badge"
                    :class="
                      form1.bdLang === 'nl' ? 'ecp__lang-badge--active' : ''
                    "
                    >NL</span
                  >
                  {{ t('editCommerce.langNl') }}
                  <span
                    v-if="translatingBd && form1.bdLang !== 'nl'"
                    class="ecp__translating-dot"
                  />
                </label>
                <textarea
                  v-model="form1.bdNl"
                  class="ecp__textarea"
                  :class="form1.bdLang === 'nl' ? 'ecp__textarea--primary' : ''"
                  rows="2"
                  :placeholder="t('editCommerce.bannerNlPlaceholder')"
                  @blur="form1.bdLang === 'nl' && translateBd()"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="ecp__divider" />

        <!-- Logo -->
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-image" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editCommerce.logoTitle') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editCommerce.logoSub') }}
              </p>
            </div>
          </div>
          <div class="ecp__logo-upload">
            <div class="ecp__logo-preview-wrap">
              <img
                v-if="logoPreview"
                :src="logoPreview"
                class="ecp__logo-img"
                alt="aperçu"
              />
              <img
                v-else-if="existingLogoUrl"
                :src="existingLogoUrl"
                class="ecp__logo-img"
                alt="logo actuel"
              />
              <div v-else class="ecp__logo-placeholder">
                <i class="pi pi-shop" />
              </div>
            </div>
            <div class="ecp__logo-actions">
              <input
                ref="logoInputRef"
                type="file"
                accept="image/*"
                class="ecp__logo-file-input"
                @change="onLogoFileChange"
              />
              <button
                class="ecp__btn ecp__btn--outline"
                @click="logoInputRef?.click()"
              >
                <i class="pi pi-upload" />
                {{
                  logoFile
                    ? t('editCommerce.changeLogo')
                    : t('editCommerce.chooseLogo')
                }}
              </button>
              <button
                v-if="logoFile"
                class="ecp__btn ecp__btn--ghost"
                @click="removeLogo"
              >
                <i class="pi pi-times" /> {{ t('editCommerce.removeLogo') }}
              </button>
              <span v-if="logoFile" class="ecp__logo-filename">{{
                logoFile.name
              }}</span>
              <span
                v-else-if="existingLogoUrl"
                class="ecp__logo-filename ecp__logo-filename--muted"
                >{{ t('editCommerce.logoKept') }}</span
              >
            </div>
          </div>
        </section>

        <div v-if="error1" class="ecp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error1 }}
        </div>

        <div class="ecp__actions">
          <button
            class="ecp__btn ecp__btn--ghost"
            @click="router.push(`/commerces/${id}`)"
          >
            <i class="pi pi-times" /> {{ t('editCommerce.cancel') }}
          </button>
          <button
            class="ecp__btn ecp__btn--primary"
            :disabled="isSaving1 || logoUploading"
            @click="submitStep1"
          >
            <span
              v-if="isSaving1 || logoUploading"
              class="ecp__spinner ecp__spinner--white"
            />
            <i v-else class="pi pi-arrow-right" />
            {{
              logoUploading
                ? t('editCommerce.uploading')
                : isSaving1
                  ? t('editCommerce.saving1')
                  : t('editCommerce.next1')
            }}
          </button>
        </div>
      </div>

      <!-- ═══ ÉTAPE 2 : Horaires ═══ -->
      <div v-else-if="step === 2" class="ecp__body">
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-clock" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editCommerce.step2Title') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editCommerce.step2Sub') }}
              </p>
            </div>
          </div>

          <div class="ecp__schedule-list">
            <div
              v-for="day in WEEKDAYS"
              :key="day"
              class="ecp__schedule-row"
              :class="{ 'ecp__schedule-row--off': !weekSchedule[day].enabled }"
            >
              <label class="ecp__toggle" @click.stop>
                <input
                  type="checkbox"
                  v-model="weekSchedule[day].enabled"
                  class="ecp__toggle-input"
                />
                <span class="ecp__toggle-track"
                  ><span class="ecp__toggle-thumb"
                /></span>
              </label>
              <span class="ecp__day-label">{{ WEEKDAY_LABELS[day] }}</span>
              <template v-if="weekSchedule[day].enabled">
                <div class="ecp__time-fields">
                  <div class="ecp__time-wrap" @click.stop>
                    <span class="ecp__time-lbl">{{
                      t('editCommerce.openTime')
                    }}</span>
                    <button
                      class="ecp__time-btn"
                      @click.stop="toggleTimePicker(`${day}-open`)"
                    >
                      {{ weekSchedule[day].open }}
                      <i
                        class="pi pi-chevron-down"
                        style="font-size: 0.65rem; margin-left: 0.25rem"
                      />
                    </button>
                    <div
                      v-if="timePickerOpen === `${day}-open`"
                      class="ecp__tp"
                      @click.stop
                    >
                      <div class="ecp__tp-col">
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustHour(day, 'open', 1)"
                        >
                          <i class="pi pi-chevron-up" />
                        </button>
                        <span class="ecp__tp-val">{{
                          parseTime(weekSchedule[day].open)
                            .h.toString()
                            .padStart(2, '0')
                        }}</span>
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustHour(day, 'open', -1)"
                        >
                          <i class="pi pi-chevron-down" />
                        </button>
                      </div>
                      <span class="ecp__tp-sep">:</span>
                      <div class="ecp__tp-col">
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustMin(day, 'open', 15)"
                        >
                          <i class="pi pi-chevron-up" />
                        </button>
                        <span class="ecp__tp-val">{{
                          parseTime(weekSchedule[day].open)
                            .m.toString()
                            .padStart(2, '0')
                        }}</span>
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustMin(day, 'open', -15)"
                        >
                          <i class="pi pi-chevron-down" />
                        </button>
                      </div>
                      <button class="ecp__tp-ok" @click="closeTimePicker">
                        <i class="pi pi-check" />
                      </button>
                    </div>
                  </div>
                  <span class="ecp__time-arrow">→</span>
                  <div class="ecp__time-wrap" @click.stop>
                    <span class="ecp__time-lbl">{{
                      t('editCommerce.closeTime')
                    }}</span>
                    <button
                      class="ecp__time-btn"
                      @click.stop="toggleTimePicker(`${day}-close`)"
                    >
                      {{ weekSchedule[day].close }}
                      <i
                        class="pi pi-chevron-down"
                        style="font-size: 0.65rem; margin-left: 0.25rem"
                      />
                    </button>
                    <div
                      v-if="timePickerOpen === `${day}-close`"
                      class="ecp__tp"
                      @click.stop
                    >
                      <div class="ecp__tp-col">
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustHour(day, 'close', 1)"
                        >
                          <i class="pi pi-chevron-up" />
                        </button>
                        <span class="ecp__tp-val">{{
                          parseTime(weekSchedule[day].close)
                            .h.toString()
                            .padStart(2, '0')
                        }}</span>
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustHour(day, 'close', -1)"
                        >
                          <i class="pi pi-chevron-down" />
                        </button>
                      </div>
                      <span class="ecp__tp-sep">:</span>
                      <div class="ecp__tp-col">
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustMin(day, 'close', 15)"
                        >
                          <i class="pi pi-chevron-up" />
                        </button>
                        <span class="ecp__tp-val">{{
                          parseTime(weekSchedule[day].close)
                            .m.toString()
                            .padStart(2, '0')
                        }}</span>
                        <button
                          class="ecp__tp-arrow"
                          @click="adjustMin(day, 'close', -15)"
                        >
                          <i class="pi pi-chevron-down" />
                        </button>
                      </div>
                      <button class="ecp__tp-ok" @click="closeTimePicker">
                        <i class="pi pi-check" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>
              <span v-else class="ecp__day-closed">{{
                t('editCommerce.closed')
              }}</span>
            </div>
          </div>
        </section>

        <div v-if="error2" class="ecp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error2 }}
        </div>

        <div class="ecp__actions">
          <button class="ecp__btn ecp__btn--ghost" @click="step = 1">
            <i class="pi pi-arrow-left" /> {{ t('editCommerce.prev2') }}
          </button>
          <button
            class="ecp__btn ecp__btn--primary"
            :disabled="isSaving2"
            @click="submitStep2"
          >
            <span v-if="isSaving2" class="ecp__spinner ecp__spinner--white" />
            <i v-else class="pi pi-arrow-right" />
            {{
              isSaving2 ? t('editCommerce.saving2') : t('editCommerce.next2')
            }}
          </button>
        </div>
      </div>

      <!-- ═══ ÉTAPE 3 : Contacts ═══ -->
      <div v-else-if="step === 3" class="ecp__body">
        <section class="ecp__section">
          <div class="ecp__section-hd">
            <div class="ecp__section-ico"><i class="pi pi-users" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editCommerce.step3Title') }}
              </p>
              <p class="ecp__section-sub">{{ t('editCommerce.step3Sub') }}</p>
            </div>
            <button
              class="ecp__add-btn"
              @click="openAddContact"
              :disabled="showContactForm"
            >
              <i class="pi pi-plus" /> {{ t('editCommerce.addContact') }}
            </button>
          </div>

          <div class="ecp__items" v-if="contacts.length > 0 || showContactForm">
            <div v-for="c in contacts" :key="c.id" class="ecp__item">
              <div class="ecp__item-avatar">
                <span>{{ initials(c) }}</span>
              </div>
              <div class="ecp__item-info">
                <div class="ecp__item-name">
                  {{ displayName(c) }}
                  <span v-if="c.isPrimary" class="ecp__item-primary">{{
                    t('editCommerce.primary')
                  }}</span>
                </div>
                <div class="ecp__item-meta">
                  <span v-if="c.position">{{
                    POSITION_LABELS[c.position] ?? c.position
                  }}</span>
                  <span v-if="c.email" class="ecp__item-sep">·</span
                  ><span v-if="c.email">{{ c.email }}</span>
                  <span v-if="c.phone" class="ecp__item-sep">·</span
                  ><span v-if="c.phone">{{ c.phone }}</span>
                </div>
              </div>
              <div class="ecp__item-actions">
                <button
                  class="ecp__icon-btn"
                  @click="openEditContact(c)"
                  :disabled="showContactForm"
                >
                  <i class="pi pi-pencil" />
                </button>
                <button
                  class="ecp__icon-btn ecp__icon-btn--danger"
                  @click="confirmContact = c"
                >
                  <i class="pi pi-trash" />
                </button>
              </div>
            </div>

            <div v-if="showContactForm" class="ecp__inline-form">
              <p class="ecp__inline-form-title">
                {{
                  editContactId
                    ? t('editCommerce.editContactTitle')
                    : t('editCommerce.newContactTitle')
                }}
              </p>
              <div class="ecp__fields">
                <div class="ecp__field-row ecp__field-row--2">
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editCommerce.lastName')
                    }}</label
                    ><input
                      v-model="contactForm.lastName"
                      class="ecp__input"
                      :class="{ 'ecp__input--error': showContactLastNameError }"
                      :placeholder="t('editCommerce.lastNamePlaceholder')"
                    />
                    <p v-if="showContactLastNameError" class="ecp__field-error">
                      {{ t('editCommerce.requiredField') }}
                    </p>
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editCommerce.firstName')
                    }}</label
                    ><input
                      v-model="contactForm.firstName"
                      class="ecp__input"
                      :class="{
                        'ecp__input--error': showContactFirstNameError,
                      }"
                      :placeholder="t('editCommerce.firstNamePlaceholder')"
                    />
                    <p
                      v-if="showContactFirstNameError"
                      class="ecp__field-error"
                    >
                      {{ t('editCommerce.requiredField') }}
                    </p>
                  </div>
                </div>
                <div class="ecp__field-row ecp__field-row--2">
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editCommerce.email')
                    }}</label
                    ><input
                      v-model="contactForm.email"
                      class="ecp__input"
                      type="email"
                      :placeholder="t('editCommerce.emailPlaceholder')"
                    />
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editCommerce.phone')
                    }}</label
                    ><input
                      v-model="contactForm.phone"
                      class="ecp__input"
                      type="tel"
                      :placeholder="t('editCommerce.phonePlaceholder')"
                    />
                  </div>
                </div>
                <div class="ecp__field-row ecp__field-row--2">
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editCommerce.position')
                    }}</label>
                    <div class="ecp__select-wrap">
                      <select
                        v-model="contactForm.position"
                        class="ecp__select"
                      >
                        <option
                          v-for="p in POSITIONS"
                          :key="p.value"
                          :value="p.value"
                        >
                          {{ p.label }}
                        </option></select
                      ><i class="pi pi-chevron-down ecp__select-icon" />
                    </div>
                  </div>
                  <div class="ecp__field ecp__field--center">
                    <label class="ecp__label">{{
                      t('editCommerce.isPrimary')
                    }}</label>
                    <label class="ecp__toggle"
                      ><input
                        type="checkbox"
                        v-model="contactForm.isPrimary"
                        class="ecp__toggle-input" /><span
                        class="ecp__toggle-track"
                        ><span class="ecp__toggle-thumb" /></span
                    ></label>
                  </div>
                </div>
              </div>
              <div v-if="contactError" class="ecp__error-banner">
                <i class="pi pi-exclamation-circle" /> {{ contactError }}
              </div>
              <div class="ecp__inline-actions">
                <button class="ecp__btn ecp__btn--ghost" @click="cancelContact">
                  <i class="pi pi-times" />
                  {{ t('editCommerce.cancelContact') }}
                </button>
                <button
                  class="ecp__btn ecp__btn--primary"
                  @click="saveContact"
                  :disabled="contactSaving"
                >
                  <span
                    v-if="contactSaving"
                    class="ecp__spinner ecp__spinner--white"
                  /><i v-else class="pi pi-check" />
                  {{
                    editContactId
                      ? t('editCommerce.editContact')
                      : t('editCommerce.addContactBtn')
                  }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="ecp__empty">
            <i class="pi pi-user ecp__empty-icon" />
            <p>{{ t('editCommerce.noContact') }}</p>
          </div>
        </section>

        <div class="ecp__actions">
          <button class="ecp__btn ecp__btn--ghost" @click="step = 2">
            <i class="pi pi-arrow-left" /> {{ t('editCommerce.prev3') }}
          </button>
          <button
            class="ecp__btn ecp__btn--primary"
            @click="router.push(`/commerces/${id}`)"
          >
            <i class="pi pi-check" /> {{ t('editCommerce.finish') }}
          </button>
        </div>
      </div>
    </template>

    <!-- Modale confirmation suppression contact -->
    <Teleport to="body">
      <Transition name="ecp-modal">
        <div
          v-if="confirmContact"
          class="ecp__modal-overlay"
          @click.self="deletingContact ? null : (confirmContact = null)"
        >
          <div class="ecp__modal" role="dialog" aria-modal="true">
            <div class="ecp__modal-icon">
              <i class="pi pi-exclamation-triangle" />
            </div>
            <h3 class="ecp__modal-title">
              {{ t('editCommerce.deleteContactTitle') }}
            </h3>
            <p class="ecp__modal-desc">
              <strong>{{ displayName(confirmContact) }}</strong>
              {{ t('editCommerce.deleteContactBody') }}
            </p>
            <div class="ecp__modal-actions">
              <button
                class="ecp__btn ecp__btn--ghost"
                @click="confirmContact = null"
                :disabled="deletingContact"
              >
                <i class="pi pi-times" /> {{ t('editCommerce.cancelDelete') }}
              </button>
              <button
                class="ecp__btn ecp__btn--danger"
                @click="confirmDeleteContact"
                :disabled="deletingContact"
              >
                <span
                  v-if="deletingContact"
                  class="ecp__spinner ecp__spinner--white"
                /><i v-else class="pi pi-trash" />
                {{
                  deletingContact
                    ? t('editCommerce.deleting')
                    : t('editCommerce.deleteBtn')
                }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.ecp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .ecp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .ecp {
    padding: 1rem;
    gap: 1rem;
  }
}
.ecp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  transition: color 0.15s;
}
.ecp__bc-link:hover {
  color: var(--nxt-primary);
}
.ecp__bc-sep {
  font-size: 0.625rem;
}
.ecp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ecp__skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: sk 1.4s infinite;
}
.ecp__sk--h {
  height: 5rem;
}
.ecp__sk--b {
  height: 22rem;
}
@keyframes sk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.ecp__load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.ecp__load-error i {
  font-size: 2rem;
  color: var(--nxt-error);
}

/* Header + stepper */
.ecp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  padding: 1.25rem 1.5rem;
  box-shadow: var(--nxt-shadow-sm);
  flex-wrap: wrap;
}
.ecp__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.ecp__back:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.ecp__header-text {
  flex: 1;
  min-width: 0;
}
.ecp__title {
  margin: 0 0 0.15rem;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.ecp__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__stepper {
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: auto;
}
.ecp__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  position: relative;
  padding: 0 1.25rem;
}
.ecp__step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 0.9rem;
  left: calc(50% + 1rem);
  width: calc(100% - 2rem);
  height: 2px;
  background: var(--nxt-outline-variant);
}
.ecp__step--done:not(:last-child)::after {
  background: var(--nxt-primary);
}
.ecp__step-dot {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid);
  border: 2px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  z-index: 1;
  position: relative;
}
.ecp__step--active .ecp__step-dot {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
.ecp__step--done .ecp__step-dot {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
  font-size: 0.6875rem;
}
.ecp__step-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.ecp__step--active .ecp__step-label,
.ecp__step--done .ecp__step-label {
  color: var(--nxt-primary);
}
@media (max-width: 600px) {
  .ecp__stepper {
    display: none;
  }
}

/* Body */
.ecp__body {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: var(--nxt-shadow-sm);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
.ecp__section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.ecp__section-hd {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.ecp__section-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
}
.ecp__section-title {
  margin: 0 0 0.1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ecp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__divider {
  height: 1px;
  background: var(--nxt-outline-variant);
}
.ecp__add-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  flex-shrink: 0;
}
.ecp__add-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.12);
}
.ecp__add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Fields */
.ecp__fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__field-row {
  display: grid;
  gap: 1rem;
}
.ecp__field-row--2 {
  grid-template-columns: 1fr 1fr;
}
.ecp__field-row--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.ecp__field--span2 {
  grid-column: span 2;
}
.ecp__field--center {
  justify-content: flex-start;
}
@media (max-width: 700px) {
  .ecp__field-row--3 {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 500px) {
  .ecp__field-row--2,
  .ecp__field-row--3 {
    grid-template-columns: 1fr;
  }
  .ecp__field--span2 {
    grid-column: span 1;
  }
}
.ecp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ecp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.ecp__req {
  color: var(--nxt-error);
}
.ecp__hint {
  font-weight: 400;
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
}
.ecp__input--error {
  border-color: var(--nxt-error, #dc2626);
}
.ecp__input--error:focus {
  border-color: var(--nxt-error, #dc2626);
}
.ecp__field-error {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-error, #dc2626);
}
.ecp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.ecp__input::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ecp__input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.ecp__input--primary {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.ecp__textarea {
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.ecp__textarea::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ecp__textarea:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.ecp__textarea--primary {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.ecp__select-wrap {
  position: relative;
}
.ecp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.ecp__select--sm {
  height: 2.125rem;
  font-size: 0.8125rem;
}
.ecp__select:focus {
  border-color: var(--nxt-primary);
}
.ecp__select-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

/* Lang badges + translating */
.ecp__translabel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.ecp__lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.25rem;
  border-radius: 0.3rem;
  font-size: 0.65rem;
  font-weight: 800;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
}
.ecp__lang-badge--active {
  background: var(--nxt-primary);
  color: #fff;
  border-color: var(--nxt-primary);
}
.ecp__translating-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--nxt-primary);
  display: inline-block;
  animation: ecp-pulse 0.8s ease-in-out infinite;
}
@keyframes ecp-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

/* Schedule */
.ecp__schedule-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.ecp__schedule-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  flex-wrap: wrap;
  transition: opacity 0.2s;
}
.ecp__schedule-row--off {
  opacity: 0.5;
}
.ecp__day-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  min-width: 5rem;
}
.ecp__day-closed {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  font-style: italic;
}
.ecp__time-fields {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;
}
.ecp__time-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}
.ecp__time-lbl {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ecp__time-btn {
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  transition: border-color 0.15s;
}
.ecp__time-btn:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.ecp__time-arrow {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__tp {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ecp__tp-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}
.ecp__tp-arrow {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  transition: background 0.12s;
}
.ecp__tp-arrow:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-primary);
}
.ecp__tp-val {
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  min-width: 2.25rem;
  text-align: center;
}
.ecp__tp-sep {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--nxt-on-surface-muted);
}
.ecp__tp-ok {
  margin-left: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: var(--nxt-primary);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

/* Toggle */
.ecp__toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.ecp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.ecp__toggle-track {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant);
  position: relative;
  transition: background 0.2s;
}
.ecp__toggle-input:checked + .ecp__toggle-track {
  background: var(--nxt-primary);
}
.ecp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.ecp__toggle-input:checked + .ecp__toggle-track .ecp__toggle-thumb {
  transform: translateX(1.125rem);
}

/* Contacts */
.ecp__items {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.ecp__item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
}
.ecp__item-avatar {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ecp__item-info {
  flex: 1;
  min-width: 0;
}
.ecp__item-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.ecp__item-primary {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  background: rgba(0, 0, 0, 0.08);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.ecp__item-meta {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.15rem;
}
.ecp__item-sep {
  opacity: 0.4;
}
.ecp__item-actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
}
.ecp__icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.ecp__icon-btn:hover:not(:disabled) {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.ecp__icon-btn--danger:hover:not(:disabled) {
  background: var(--nxt-error-container, #fff0f0);
  color: var(--nxt-error);
  border-color: var(--nxt-error);
}
.ecp__icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ecp__inline-form {
  background: var(--nxt-surface-low);
  border: 1.5px solid var(--nxt-primary);
  border-radius: 0.875rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__inline-form-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-primary);
}
.ecp__inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.ecp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  text-align: center;
}
.ecp__empty-icon {
  font-size: 1.5rem;
  opacity: 0.25;
}
.ecp__empty p {
  margin: 0;
}

/* Buttons + misc */
.ecp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.ecp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ecp__btn--primary {
  background: var(--nxt-gradient-primary);
  color: var(--nxt-on-primary);
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.ecp__btn--primary:not(:disabled):hover {
  filter: brightness(1.07);
}
.ecp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.ecp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.ecp__btn--outline {
  background: transparent;
  color: var(--nxt-primary);
  border: 1px solid var(--nxt-primary);
}
.ecp__btn--outline:hover {
  background: rgba(0, 0, 0, 0.07);
}

/* Logo upload */
.ecp__logo-upload {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.ecp__logo-preview-wrap {
  width: 5rem;
  height: 5rem;
  flex-shrink: 0;
  border-radius: 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ecp__logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ecp__logo-placeholder {
  font-size: 1.75rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.35;
}
.ecp__logo-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.ecp__logo-file-input {
  display: none;
}
.ecp__logo-filename {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__logo-filename--muted {
  font-style: italic;
}
.ecp__btn--danger {
  background: var(--nxt-error, #e53935);
  color: #fff;
}
.ecp__btn--danger:not(:disabled):hover {
  filter: brightness(1.1);
}
.ecp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.ecp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container, #fff0f0);
  border: 1px solid var(--nxt-error, #e53935);
  font-size: 0.875rem;
  color: var(--nxt-error, #e53935);
}
.ecp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: spin 0.7s linear infinite;
}
.ecp__spinner--white {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: var(--nxt-on-primary);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal */
.ecp__modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.ecp__modal {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}
.ecp__modal-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  border: 2px solid rgba(229, 57, 53, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-error, #e53935);
}
.ecp__modal-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.ecp__modal-desc {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.ecp__modal-desc strong {
  color: var(--nxt-on-surface);
  font-weight: 700;
}
.ecp__modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
  width: 100%;
  margin-top: 0.25rem;
}
.ecp-modal-enter-active,
.ecp-modal-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.ecp-modal-enter-from,
.ecp-modal-leave-to {
  opacity: 0;
}
.ecp-modal-enter-from .ecp__modal,
.ecp-modal-leave-to .ecp__modal {
  transform: scale(0.94) translateY(8px);
}
</style>
